import React, { Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { CatalogProvider } from '@/context/CatalogContext';

// ---------------------------------------------------------------------------
// Page imports — eager vs lazy
// ---------------------------------------------------------------------------
// The four pages below are imported eagerly because they are the most common
// "entry points" right after a token-validated boot:
//   • LoginPage          → unauthenticated landing page
//   • DashboardPage      → first thing a logged-in agency user sees
//   • PortaLetterePage   → first thing a logged-in portalettere sees (TC27)
//   • AppShellTopNav     → the chrome around every authenticated route
// Importing them eagerly avoids a flash of a loader on these critical paths.
//
// Every other page is lazy-loaded so it ships its own chunk and is only
// downloaded when the user actually navigates to it. On a TC27 with a 4G
// connection this typically cuts the initial JS payload by 60-75%.
import AppShellTopNav from '@/components/AppShellTopNav';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import PortaLetterePage from '@/pages/PortaLetterePage';

const NuovaRaccomandataPage = lazy(() => import('@/pages/NuovaRaccomandataPage'));
const ModificaRaccomandataPage = lazy(() => import('@/pages/ModificaRaccomandataPage'));
const CercaRaccomandataPage = lazy(() => import('@/pages/CercaRaccomandataPage'));
const TotaleGiornalieroPage = lazy(() => import('@/pages/TotaleGiornalieroPage'));
const TotaleMensilePage = lazy(() => import('@/pages/TotaleMensilePage'));
const PrezziPage = lazy(() => import('@/pages/PrezziPage'));
const CostiAgenziaPage = lazy(() => import('@/pages/CostiAgenziaPage'));
const ModuliBolgettaPage = lazy(() => import('@/pages/ModuliBolgettaPage'));
const AccettazioneBolgettaPage = lazy(() => import('@/pages/AccettazioneBolgettaPage'));
const DistintaAnaliticaPage = lazy(() => import('@/pages/DistintaAnaliticaPage'));
const ImpostazioniCorrieriPage = lazy(() => import('@/pages/ImpostazioniCorrieriPage'));
const AdminUtentiPage = lazy(() => import('@/pages/AdminUtentiPage'));
const AnnunciAdminPage = lazy(() => import('@/pages/AnnunciAdminPage'));
const ImpostazioniPage = lazy(() => import('@/pages/ImpostazioniPage'));
const PublicTrackingPage = lazy(() => import('@/pages/PublicTrackingPage'));
const PublicTrackingSearchPage = lazy(() => import('@/pages/PublicTrackingSearchPage'));
const ClientiBusinessPage = lazy(() => import('@/pages/ClientiBusinessPage'));
const ClienteBusinessDetailPage = lazy(() => import('@/pages/ClienteBusinessDetailPage'));
const PortalettereAdminPage = lazy(() => import('@/pages/PortalettereAdminPage'));
const RitiroSportelloPage = lazy(() => import('@/pages/RitiroSportelloPage'));
const NetworkBillingAdminPage = lazy(() => import('@/pages/NetworkBillingAdminPage'));
const NetworkBillingUserPage = lazy(() => import('@/pages/NetworkBillingUserPage'));
const PostaMassivaPage = lazy(() => import('@/pages/PostaMassivaPage'));
const ImportPage = lazy(() => import('@/pages/ImportPage'));
const PezziAgenziePage = lazy(() => import('@/pages/PezziAgenziePage'));

/** Minimal loader shown while a lazy chunk is being fetched. Kept very small
 *  (no card, no skeleton) so it doesn't itself bring in extra UI weight. */
function RouteFallback() {
  return (
    <div className="p-8 text-sm text-[color:var(--text-secondary)] flex items-center gap-2" data-testid="route-fallback">
      <div className="h-3 w-3 rounded-full bg-[color:var(--brand-orange)] animate-pulse" />
      Caricamento pagina…
    </div>
  );
}

const Protected = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[color:var(--surface-2)]" data-testid="boot-loader">
      <div className="flex items-center gap-3 text-[color:var(--text-secondary)]">
        <div className="h-3 w-3 rounded-full bg-[color:var(--brand-orange)] animate-pulse" />
        <div className="h-3 w-3 rounded-full bg-[color:var(--brand-orange)] animate-pulse" style={{ animationDelay: '150ms' }} />
        <div className="h-3 w-3 rounded-full bg-[color:var(--brand-orange)] animate-pulse" style={{ animationDelay: '300ms' }} />
      </div>
      <div className="mt-4 text-sm font-medium">Caricamento in corso…</div>
      <div className="mt-1 text-xs text-[color:var(--text-secondary)] max-w-xs text-center">Se è il primo accesso della giornata può richiedere qualche secondo.</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Portalettere can only access /m and tracking - force redirect
  if (user.role === 'portalettere' && typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (!path.startsWith('/m') && !path.startsWith('/track/')) {
      return <Navigate to="/m" replace />;
    }
  }
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CatalogProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/track" element={<PublicTrackingSearchPage />} />
                <Route path="/track/:code" element={<PublicTrackingPage />} />
                <Route path="/m" element={<Protected><PortaLetterePage /></Protected>} />
                <Route element={<Protected><AppShellTopNav /></Protected>}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/raccomandate/nuova" element={<NuovaRaccomandataPage />} />
                  <Route path="/raccomandate/cerca" element={<CercaRaccomandataPage />} />
                  <Route path="/raccomandate/ritiro" element={<RitiroSportelloPage />} />
                  <Route path="/raccomandate/:id/modifica" element={<ModificaRaccomandataPage />} />
                  <Route path="/posta-massiva" element={<PostaMassivaPage />} />
                  <Route path="/importa" element={<ImportPage />} />
                  <Route path="/clienti-business" element={<ClientiBusinessPage />} />
                  <Route path="/clienti-business/:id" element={<ClienteBusinessDetailPage />} />
                  <Route path="/portalettere" element={<PortalettereAdminPage />} />
                  <Route path="/totali/giornaliero" element={<TotaleGiornalieroPage />} />
                  <Route path="/totali/mensile" element={<TotaleMensilePage />} />
                  <Route path="/prezzi" element={<PrezziPage />} />
                  <Route path="/costi-agenzia" element={<Protected role="superadmin"><CostiAgenziaPage /></Protected>} />
                  <Route path="/moduli" element={<ModuliBolgettaPage />} />
                  <Route path="/moduli/accettazione-bolgetta" element={<AccettazioneBolgettaPage />} />
                  <Route path="/moduli/distinta-analitica" element={<DistintaAnaliticaPage />} />
                  <Route path="/impostazioni" element={<ImpostazioniPage />} />
                  <Route path="/impostazioni/corrieri" element={<Protected role="superadmin"><ImpostazioniCorrieriPage /></Protected>} />
                  <Route path="/admin/utenti" element={<Protected role="superadmin"><AdminUtentiPage /></Protected>} />
                  <Route path="/admin/annunci" element={<Protected role="superadmin"><AnnunciAdminPage /></Protected>} />
                  <Route path="/admin/fatture-rete" element={<Protected role="superadmin"><NetworkBillingAdminPage /></Protected>} />
                  <Route path="/admin/pezzi-agenzie" element={<Protected role="superadmin"><PezziAgenziePage /></Protected>} />
                  <Route path="/fatturazione-rete" element={<NetworkBillingUserPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
        </CatalogProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
